import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { HubIcon } from '@atlaskit/logo';
import { LinkMenuItem, type LinkMenuItemProps } from '@atlaskit/side-nav-items/link-menu-item';

import type { CustomItemProps } from '@atlassian/navigation-common/types';

import { fg } from '@confluence/feature-gating';

import type { MenuId } from '../../../PlatformNavigationTypes.ts';

import { AbstractCompanyHubItem } from './AbstractCompanyHubItem.tsx';

// TODO: This type will most liekly be refactored to platform configuration in the future, but for now we want to keep it as a separate component in case there are any specific logic or analytics we want to add for the company hub shortcut.
type GlobalItemProps = {
	setIsCustomizeSidebarDialogVisible?: (isCustomizeSidebarDialogVisible: boolean) => void;
	isHidden?: boolean;
	peekingId?: MenuId;
	setPeekingId?: (menuId?: MenuId) => void;
};

const DEFAULT_COMPANY_HUB_NAME = 'Company Hub';

const i18n = defineMessages({
	CompanyHubName: {
		id: 'side-navigation.custom.company-hub.shortcut-name',
		description: 'name to custom company hub link',
		defaultMessage: 'Company Hub',
	},
});

export const CompanyHubItem: FC<CompanyHubItemProps> = ({ isHidden, onClick, ...rest }) => {
	// This item can only be hidden if it's an app shortcut, but then it doesn't peek because it opens in a new tab.
	if (isHidden) {
		return null;
	}

	return (
		<AbstractCompanyHubItem navVersion="4">
			{({ companyHubName, isSelected, namedRoute, onClick: onAbstractCompanyHubItemClick }) => {
				const target: LinkMenuItemProps['target'] = '_blank';
				// https://hello.atlassian.net/wiki/spaces/DST/pages/4992197568/Company+Hub+Team+25+App+Tile+update+worksheet and
				// https://www.figma.com/design/MrViCsb93b2UqRjYrGQgaD/Confluence-Griffin-nav?node-id=9932-52111&t=B9QrCfJUvgDZwy64-0
				// say the icon should be 20px.
				const elemBefore: LinkMenuItemProps['elemBefore'] = <HubIcon size="xsmall" />;
				const elemAfter: LinkMenuItemProps['elemAfter'] = (
					<LinkExternalIcon label="" spacing="spacious" size="small" />
				);

				const onMenuLinkItemClick: LinkMenuItemProps['onClick'] = () => {
					onClick?.();
					onAbstractCompanyHubItemClick();
				};

				const isDefaultCompanyHubName = companyHubName === DEFAULT_COMPANY_HUB_NAME;

				if (fg('navx-global-side-nav-confluence')) {
					const {
						peekingId: _peekingId,
						setPeekingId: _setPeekingId,
						draggableRef,
						...dndProps
					} = rest;

					return (
						<LinkMenuItem
							ref={draggableRef}
							elemAfter={elemAfter}
							elemBefore={elemBefore}
							href={namedRoute?.toUrl() || '#'}
							isSelected={isSelected}
							onClick={onMenuLinkItemClick}
							target={target}
							{...dndProps}
						>
							{isDefaultCompanyHubName ? (
								<FormattedMessage {...i18n.CompanyHubName} />
							) : (
								companyHubName
							)}
						</LinkMenuItem>
					);
				}

				return (
					<LinkMenuItem
						elemAfter={elemAfter}
						elemBefore={elemBefore}
						href={namedRoute?.toUrl() || '#'}
						isSelected={isSelected}
						onClick={onMenuLinkItemClick}
						target={target}
					>
						{isDefaultCompanyHubName ? (
							<FormattedMessage {...i18n.CompanyHubName} />
						) : (
							companyHubName
						)}
					</LinkMenuItem>
				);
			}}
		</AbstractCompanyHubItem>
	);
};

type CompanyHubItemProps = CustomItemProps &
	GlobalItemProps & {
		onClick?: () => void;
	};
